/**
 * Sale status display vocabulary for Lightspeed and Xero.
 *
 * Single source of truth for the chip label + colour shown in the
 * inspector. The cross-system *equivalence* mapping (LS_PAID == Xero_PAID
 * for the purpose of the match check) lives in the backend's
 * `shared/sale_alignment.py` (`EXPECTED_XERO_FOR_LS_STATUS`) and reaches
 * the frontend as the `ls_standard_sale_status` / `xero_standard_sale_status`
 * row fields. This module is concerned only with the rendered chip —
 * labels and colours can be more granular than the equivalence (e.g.
 * distinguishing "Closed" from "Paid" on the LS side) without affecting
 * match logic.
 *
 * The `LS_TO_EXPECTED_XERO` map below is also re-exported for the rare
 * frontend code paths that don't get a backend match boolean and must
 * derive the equivalence locally (see `saleMismatchIssues.ts`). Mirror
 * the backend dict if you change either.
 *
 * Consumers:
 * - `frontend/src/config/exceptions/sales/compareExceptions.ts` (table cell)
 * - `frontend/src/pages/inspector/shared/Exceptions/CompareSalesView.tsx`
 *   (side-by-side panel header)
 * - `frontend/src/utils/saleMismatchIssues.ts` (status equivalence fallback)
 */

export type StatusChipColor = 'default' | 'success' | 'warning' | 'error' | 'info'

/**
 * Status chip palette. All chips render outlined (coloured border +
 * coloured text on a transparent background) so the status column
 * doesn't dominate the row visually — analysts read the row by its
 * mismatch markers, not by the largest filled rectangle on screen.
 */
export interface SaleStatusDisplay {
  label: string
  color: StatusChipColor
}

const LS_STATUS_DISPLAY: Record<string, SaleStatusDisplay> = {
  ONACCOUNT: { label: 'Open', color: 'info' },
  ONACCOUNT_CLOSED: { label: 'Closed', color: 'success' },
  CLOSED: { label: 'Closed', color: 'success' },
  VOIDED: { label: 'Voided', color: 'error' },
}

const XERO_STATUS_DISPLAY: Record<string, SaleStatusDisplay> = {
  AUTHORISED: { label: 'Authorised', color: 'info' },
  PAID: { label: 'Paid', color: 'success' },
  VOIDED: { label: 'Voided', color: 'error' },
  DELETED: { label: 'Deleted', color: 'error' },
  DRAFT: { label: 'Draft', color: 'default' },
  SUBMITTED: { label: 'Submitted', color: 'default' },
}

/**
 * What the Xero status *should* be given the current LS status. Used to
 * render an "expected" delta chip on the Xero side when the actual Xero
 * status doesn't match what LS implies. Mirrors the canonical
 * equivalence map in `backend/shared/sale_alignment.py`
 * (`EXPECTED_XERO_FOR_LS_STATUS`):
 *   LS Open / On Account     → Xero Authorised
 *   LS Closed / On Account Closed → Xero Paid
 *   LS Voided                → Xero Voided (or Deleted, equivalent)
 *
 * Exported (not module-private) so `saleMismatchIssues.ts` can reuse
 * the same map without redeclaring it.
 */
export const LS_TO_EXPECTED_XERO: Record<string, string> = {
  ONACCOUNT: 'AUTHORISED',
  ONACCOUNT_CLOSED: 'PAID',
  CLOSED: 'PAID',
  VOIDED: 'VOIDED',
}

/**
 * Resolve the display label + chip colour for a sale status.
 *
 * Returns null for empty/missing input so callers can branch on
 * "no status to render". Unknown statuses fall through to a default
 * chip showing the raw value — preferable to swallowing it.
 */
export function statusDisplay(
  system: 'lightspeed' | 'xero',
  raw?: string | null,
): SaleStatusDisplay | null {
  if (!raw) return null
  const upper = raw.toUpperCase()
  const map = system === 'lightspeed' ? LS_STATUS_DISPLAY : XERO_STATUS_DISPLAY
  return map[upper] ?? { label: raw, color: 'default' }
}

/**
 * Expected Xero status given the LS status. Returns null when the LS
 * status isn't in the canonical map (analyst will see the actual chip
 * with no expected-state hint, which is the safest default).
 */
export function expectedXeroStatus(lsStatus?: string | null): SaleStatusDisplay | null {
  if (!lsStatus) return null
  const expected = LS_TO_EXPECTED_XERO[lsStatus.toUpperCase()]
  if (!expected) return null
  return statusDisplay('xero', expected)
}
